import React, { useState } from 'react';
import { Mail, CheckCircle, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useTranslation } from '../i18n';

interface NewsletterFormProps {
  variant?: 'inline' | 'centered' | 'footer';
  showLabels?: boolean;
}

export default function NewsletterForm({ variant = 'inline', showLabels = false }: NewsletterFormProps) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    company: '',
    phone: ''
  });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');

    try {
      const { error } = await supabase
        .from('newsletter_subscribers')
        .insert([{
          email: formData.email,
          first_name: formData.first_name || null,
          last_name: formData.last_name || null,
          company: formData.company || null,
          phone: formData.phone || null,
          source: 'newsletter_form',
          status: 'active'
        }]);

      if (error) {
        if (error.code === '23505') {
          setMessage(t.footer.newsletter.error);
          setStatus('error');
        } else {
          throw error;
        }
      } else {
        setMessage(t.footer.newsletter.success);
        setStatus('success');
        setFormData({ email: '', first_name: '', last_name: '', company: '', phone: '' });
      }
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      setMessage(t.footer.newsletter.error);
      setStatus('error');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  if (status === 'success') {
    return (
      <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
        <p className="text-green-800">{message}</p>
      </div>
    );
  }

  if (variant === 'inline') {
    return (
      <form onSubmit={handleSubmit} className="w-full max-w-md">
        <div className="flex gap-2">
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder={t.footer.newsletter.placeholder}
            required
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <button
            type="submit"
            disabled={status === 'loading'}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Mail className="w-4 h-4" />
            {status === 'loading' ? t.footer.newsletter.button + '...' : t.footer.newsletter.button}
          </button>
        </div>
        {status === 'error' && (
          <div className="flex items-center gap-2 mt-2 text-red-600 text-sm">
            <AlertCircle className="w-4 h-4" />
            <span>{message}</span>
          </div>
        )}
      </form>
    );
  }

  if (variant === 'footer') {
    return (
      <form onSubmit={handleSubmit} className="w-full">
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder={t.footer.newsletter.placeholder}
            required
            className="flex-1 px-4 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <button
            type="submit"
            disabled={status === 'loading'}
            className="px-6 py-2 bg-white text-blue-600 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap"
          >
            <Mail className="w-4 h-4" />
            {status === 'loading' ? t.footer.newsletter.button + '...' : t.footer.newsletter.button}
          </button>
        </div>
        {status === 'error' && (
          <div className="flex items-center gap-2 mt-2 text-red-300 text-sm">
            <AlertCircle className="w-4 h-4" />
            <span>{message}</span>
          </div>
        )}
      </form>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="w-full max-w-lg space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          {showLabels && <label className="block text-sm font-medium text-gray-700 mb-1">Prenume</label>}
          <input
            type="text"
            name="first_name"
            value={formData.first_name}
            onChange={handleChange}
            placeholder="Prenume"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          {showLabels && <label className="block text-sm font-medium text-gray-700 mb-1">Nume</label>}
          <input
            type="text"
            name="last_name"
            value={formData.last_name}
            onChange={handleChange}
            placeholder="Nume"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>
      <div>
        {showLabels && <label className="block text-sm font-medium text-gray-700 mb-1">Companie</label>}
        <input
          type="text"
          name="company"
          value={formData.company}
          onChange={handleChange}
          placeholder="Companie"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>
      <div>
        {showLabels && <label className="block text-sm font-medium text-gray-700 mb-1">Telefon</label>}
        <input
          type="tel"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          placeholder="Telefon (opțional)"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>
      <div>
        {showLabels && <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>}
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="Email *"
          required
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>
      <button
        type="submit"
        disabled={status === 'loading'}
        className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        <Mail className="w-5 h-5" />
        {status === 'loading' ? t.footer.newsletter.button + '...' : t.footer.newsletter.button}
      </button>
      {status === 'error' && (
        <div className="flex items-center gap-2 text-red-600 text-sm">
          <AlertCircle className="w-4 h-4" />
          <span>{message}</span>
        </div>
      )}
      <p className="text-xs text-gray-500">
        Prin abonare, ești de acord să primești emailuri de la ControlMax. Te poți dezabona oricând.
      </p>
    </form>
  );
}